// The one PDF opener (web, MCP, scripts and the phone): pdf.js from unpdf (the serverless build, pure JS, no
// worker file, no native module) reads the text items of each page, and the pure parsers (./pdf-items.ts and the
// per-bank *-pdf.ts) turn them into rows. The file never leaves the device; the password is used once, to open
// the file, and is never stored or logged. Hermes needs `installPdfPolyfills()` (./pdf-polyfills.ts) first.
import type { ParsedStatement } from "@paisa/types"
import { parseAmexCreditCardItems } from "./amex/credit-card-pdf"
import { type GeneralRead, readGeneralStatement } from "./general/statement"
import { parseHdfcCreditCardItems } from "./hdfc/cc-pdf"
import { parseHdfcCCYearlyItems } from "./hdfc/cc-yearly-pdf"
import { parseHdfcSavingsStatementItems } from "./hdfc/savings-pdf"
import { type PdfDoc, type PdfItem, type PdfItems, parsePdfText, pdfItemsOf } from "./pdf-items"
import { parseSbiSavingsItems } from "./sbi/savings-pdf"

/** The file needs a password (`wrong` false) or the one given is wrong. Named like pdf.js's own exception. */
export class PdfPasswordError extends Error {
  override readonly name = "PasswordException"
  constructor(readonly wrong: boolean) {
    super(wrong ? "Incorrect Password" : "No password given")
  }
}

/** Not a PDF pdf.js can open. */
export class PdfInvalidError extends Error {
  override readonly name = "InvalidPDFException"
  constructor(message = "Invalid PDF") {
    super(message)
  }
}

/** Reading took longer than `timeoutMs`; pdf.js's work was stopped. */
export class PdfTimeoutError extends Error {
  override readonly name = "PdfTimeoutError"
  constructor() {
    super("Reading the PDF timed out")
  }
}

type Task = { promise: Promise<PdfDoc>; destroy: () => Promise<void> | void }
/** Opens the file with pdf.js; swapped in tests. */
export type PdfLoader = (data: Uint8Array, password?: string) => Promise<Task>

export interface ReadPdfOptions {
  password?: string
  /** Default 30 s. */
  timeoutMs?: number
  /** Test seam: replaces pdf.js. */
  load?: PdfLoader
}

/** Loaded on first use: it is ~1.6 MB of JS that callers who never read a PDF don't pay for. */
const loadWithPdfjs: PdfLoader = async (data, password) => {
  const { getDocument } = await import("unpdf/pdfjs")
  return getDocument({
    data,
    useSystemFonts: true,
    ...(password ? { password } : {}),
  }) as unknown as Task
}

/** A pdf.js PasswordException: code 1 = needs one, 2 = the one given is wrong. */
const asPassword = (e: unknown) => {
  const x = e as { name?: string; code?: number } | null
  return x?.name === "PasswordException" ? new PdfPasswordError(x.code === 2) : null
}

/**
 * The text items of a PDF. Throws PdfPasswordError, PdfInvalidError (a file pdf.js can't open) or
 * PdfTimeoutError. A timeout destroys the pdf.js task, which stops the work.
 */
export async function readPdf(bytes: Uint8Array, opts: ReadPdfOptions = {}): Promise<PdfItems> {
  const { password, timeoutMs = 30_000, load = loadWithPdfjs } = opts
  let task: Task | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new PdfTimeoutError()), timeoutMs)
  })
  const work = (async () => {
    try {
      // a copy: pdf.js may detach the buffer it is given, and the caller keeps theirs
      task = await load(bytes.slice(), password)
      return await pdfItemsOf(await task.promise)
    } catch (e) {
      throw asPassword(e) ?? new PdfInvalidError(e instanceof Error ? e.message : undefined)
    }
  })()
  try {
    return await Promise.race([work, timeout])
  } finally {
    clearTimeout(timer)
    work.catch(() => {}) // a run that lost the race to the timeout must not surface as unhandled
    // Frees pdf.js's worker state either way; a timed-out run stops here.
    Promise.resolve(task?.destroy()).catch(() => {})
  }
}

/** readPdf, then the bank parser that recognises the items (null: none does). */
export async function parsePdfFile(
  bytes: Uint8Array,
  opts?: ReadPdfOptions,
): Promise<ParsedStatement | null> {
  return parsePdfText(await readPdf(bytes, opts))
}

/**
 * The general reader on a PDF file: a password PDF without the right password is `password`, one pdf.js can't
 * open is `no-table`. Bank parsers are not tried (parsePdfFile tries them first).
 */
export async function readGeneralPdf(
  bytes: Uint8Array,
  opts?: ReadPdfOptions,
): Promise<GeneralRead> {
  try {
    return readGeneralStatement(await readPdf(bytes, opts))
  } catch (e) {
    if (e instanceof PdfPasswordError) return { ok: false, reason: "password" }
    if (e instanceof PdfInvalidError) return { ok: false, reason: "no-table" }
    throw e
  }
}

const viaPdf =
  (parse: (items: PdfItem[]) => ParsedStatement) =>
  async (data: ArrayBuffer, password?: string): Promise<ParsedStatement> =>
    parse(await readPdf(new Uint8Array(data), { password }))

export const parseHdfcSavingsPdf = viaPdf(parseHdfcSavingsStatementItems)
export const parseHdfcCreditCardPdf = viaPdf(parseHdfcCreditCardItems)
export const parseHdfcCCYearlyPdf = viaPdf(parseHdfcCCYearlyItems)
export const parseAmexCreditCardPdf = viaPdf(parseAmexCreditCardItems)
export const parseSbiSavingsPdf = viaPdf(parseSbiSavingsItems)
